// Talks to the 01POS desktop app (a WebView2 shell) through window.printBridge.
// In a plain browser the bridge does not exist and receipts fall back to the
// browser print dialog (see printReceipt.js).
//
// Bridge contract (receipt v1, agreed with the desktop app):
//   listPrinters()        -> [{ name, status, isAvailable }]
//   printReceipt(payload) -> { message, jobId, printerName, status }

// Kept per till (localStorage), not per cashier, so it survives logout.
const PRINTER_STORAGE_KEY = "01pos_receipt_printer";
// Extra printers (Pro): see normalizeRoutes for the shape.
const ROUTES_STORAGE_KEY = "01pos_printer_routes";

const LIST_TIMEOUT_MS = 8000;
const PRINT_TIMEOUT_MS = 10000;

export class BridgeTimeoutError extends Error {
  constructor(action) {
    super(`The printer service did not respond (${action}).`);
    this.name = "BridgeTimeoutError";
  }
}

export const hasPrintBridge = () =>
  typeof window !== "undefined" &&
  !!window.printBridge &&
  typeof window.printBridge.listPrinters === "function";

// Some bridge errors may never be answered, so every call gets a deadline.
const withTimeout = (promise, ms, action) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new BridgeTimeoutError(action)), ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

export const listPrinters = async () => {
  if (!hasPrintBridge()) throw new Error("Printer service is not available.");
  const printers = await withTimeout(
    window.printBridge.listPrinters(),
    LIST_TIMEOUT_MS,
    "listPrinters"
  );
  return Array.isArray(printers) ? printers : [];
};

export const sendToPrinter = (payload) => {
  if (!hasPrintBridge()) throw new Error("Printer service is not available.");
  const send = window.printBridge.printReceipt || window.printBridge.print;
  return withTimeout(
    send.call(window.printBridge, payload),
    PRINT_TIMEOUT_MS,
    "printReceipt"
  );
};

export const getSavedPrinter = () => {
  try {
    return localStorage.getItem(PRINTER_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

export const savePrinter = (name) => {
  try {
    if (name) localStorage.setItem(PRINTER_STORAGE_KEY, name);
    else localStorage.removeItem(PRINTER_STORAGE_KEY);
    window.dispatchEvent(new Event("receipt-printer-changed"));
  } catch {
    // Storage unavailable: the cashier gets the browser dialog instead.
  }
};

export const newRouteId = () =>
  `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// An extra printer ("route"):
//   { id, printerName, type: "slip" | "receipt", items: "assigned" | "all",
//     categoryIds: [number], printOn: { draft: bool, paid: bool } }
// type "slip" is a kitchen/bar slip (no prices); "receipt" is the full customer
// receipt and always takes all items. A category can be in at most one
// "assigned" slip route ("all" routes reserve nothing). One route per printer.
// Anything malformed is dropped, so bad storage can never break printing.
export const normalizeRoutes = (routes) => {
  const takenCategories = new Set();
  const usedPrinters = new Set();
  const usedIds = new Set();
  const out = [];

  for (const route of Array.isArray(routes) ? routes : []) {
    const printerName =
      typeof route?.printerName === "string" ? route.printerName.trim() : "";
    if (!printerName || usedPrinters.has(printerName)) continue;

    const type = route?.type === "receipt" ? "receipt" : "slip";
    const items = type === "receipt" || route?.items === "all" ? "all" : "assigned";
    const printOn = { draft: !!route?.printOn?.draft, paid: !!route?.printOn?.paid };
    if (!printOn.draft && !printOn.paid) continue;

    let categoryIds = [];
    if (type === "slip" && items === "assigned") {
      for (const raw of Array.isArray(route?.categoryIds) ? route.categoryIds : []) {
        const id = Number(raw);
        if (raw === null || raw === "" || !Number.isFinite(id)) continue;
        if (takenCategories.has(id) || categoryIds.includes(id)) continue;
        categoryIds.push(id);
      }
      if (categoryIds.length === 0) continue;
      categoryIds.forEach((id) => takenCategories.add(id));
    }

    let id = typeof route?.id === "string" && route.id ? route.id : newRouteId();
    while (usedIds.has(id)) id = newRouteId();
    usedIds.add(id);
    usedPrinters.add(printerName);
    out.push({ id, printerName, type, items, categoryIds, printOn });
  }
  return out;
};

export const getPrinterRoutes = () => {
  try {
    return normalizeRoutes(JSON.parse(localStorage.getItem(ROUTES_STORAGE_KEY)));
  } catch {
    return [];
  }
};

export const savePrinterRoutes = (routes) => {
  try {
    const clean = normalizeRoutes(routes);
    if (clean.length) localStorage.setItem(ROUTES_STORAGE_KEY, JSON.stringify(clean));
    else localStorage.removeItem(ROUTES_STORAGE_KEY);
    window.dispatchEvent(new Event("printer-routes-changed"));
  } catch {
    // Storage unavailable: extra printers simply don't print.
  }
};
